import React, { useState } from "react";
import "./login.css";
import Logo from "src/Assets/images/logo.png";
import API from "src/API3";
import { toast } from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcUser  = () => <Svg size={16}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const IcLock  = () => <Svg size={16}><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Svg>;
const IcEye   = () => <Svg size={16}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></Svg>;
const IcEyeOff = () => <Svg size={16}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></Svg>;
const IcArrow = () => <Svg size={16}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></Svg>;
const IcWA    = () => <Svg size={16}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" /></Svg>;
const IcShield = () => <Svg size={18}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></Svg>;
const IcZap    = () => <Svg size={18}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></Svg>;
const IcBed    = () => <Svg size={18}><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;
const IcAlert  = () => <Svg size={14}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></Svg>;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [touched, setTouched] = useState({ email: false, password: false });

  const validateEmail = (value) => {
    if (!value) return "Username is required";
    const re = /^[a-zA-Z0-9_]{4,}$/;
    return re.test(value) ? "" : "Username must be at least 4 characters";
  };
  const validatePassword = (value) => {
    if (!value) return "Password is required";
    if (value.length < 4) return "Password must be at least 4 characters";
    return "";
  };

  const handleChange = (field, value) => {
    if (field === "email") {
      setEmail(value);
      if (touched.email) setErrors((p) => ({ ...p, email: validateEmail(value) }));
    } else if (field === "password") {
      setPassword(value);
      if (touched.password) setErrors((p) => ({ ...p, password: validatePassword(value) }));
    }
  };

  const handleBlur = (field) => {
    setTouched((p) => ({ ...p, [field]: true }));
    if (field === "email") setErrors((p) => ({ ...p, email: validateEmail(email) }));
    else if (field === "password") setErrors((p) => ({ ...p, password: validatePassword(password) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    setErrors({ email: emailError, password: passwordError });

    if (emailError || passwordError) return;
    setSubmitting(true);
    try {
      const resp = await API.post("properties/login", { owner: email, password });
      localStorage.setItem("accesstokenhotelparkmall", resp.data.token);
      toast.success("Redirecting to dashboard");
      setTimeout(() => { window.location = "/"; }, 700);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Something went wrong, please contact the admin",
      );
      setSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const phoneNumber = "+213775740077";
    const message = "I need help with my Protour staff account";
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\s+/g, "")}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="lg-page">
      {/* ── Left brand panel ── */}
      <aside className="lg-brand">
        <div className="lg-brand__top">
          <div className="lg-brand__logo">
            <img src={Logo} alt="Protour" />
          </div>
          <div className="lg-brand__title">
            <strong>Protour</strong>
            <span>Staff portal</span>
          </div>
        </div>

        <div className="lg-brand__hero">
          <span className="lg-brand__eyebrow">Property management</span>
          <h2>Run your property from a single, calm dashboard.</h2>
          <p>
            Reservations, rates, room inventory and content — all in one place,
            built for hotel teams who move fast.
          </p>
        </div>

        <ul className="lg-features">
          <li><span className="lg-features__icon"><IcBed /></span><div><strong>Live availability</strong><span>Floor-by-floor view with realtime occupancy.</span></div></li>
          <li><span className="lg-features__icon"><IcZap /></span><div><strong>Rate management</strong><span>Pricing matrix per room type, working days and offers.</span></div></li>
          <li><span className="lg-features__icon"><IcShield /></span><div><strong>Secure access</strong><span>Role-based control over every staff action.</span></div></li>
        </ul>

        <span className="lg-brand__foot">© {new Date().getFullYear()} Protour · All rights reserved</span>
      </aside>

      {/* ── Right form panel ── */}
      <main className="lg-form-wrap">
        <form className="lg-form" onSubmit={handleSubmit} noValidate>
          <div className="lg-form__head">
            <span className="lg-form__eyebrow">Welcome back</span>
            <h1>Sign in to your portal</h1>
            <p>Enter your staff credentials to access the dashboard.</p>
          </div>

          <label className="lg-field">
            <span>Username</span>
            <div className={`lg-input ${touched.email && errors.email ? "is-error" : ""}`}>
              <IcUser />
              <input
                type="text"
                value={email}
                placeholder="your.username"
                onChange={(e) => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                autoComplete="username"
                autoFocus
              />
            </div>
            {touched.email && errors.email && (
              <span className="lg-error"><IcAlert /> {errors.email}</span>
            )}
          </label>

          <label className="lg-field">
            <span>
              Password
              <button
                type="button"
                className="lg-help-link"
                onClick={handleWhatsAppContact}
              >
                Forgot password?
              </button>
            </span>
            <div className={`lg-input ${touched.password && errors.password ? "is-error" : ""}`}>
              <IcLock />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                placeholder="••••••••"
                onChange={(e) => handleChange("password", e.target.value)}
                onBlur={() => handleBlur("password")}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="lg-input__toggle"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <IcEyeOff /> : <IcEye />}
              </button>
            </div>
            {touched.password && errors.password && (
              <span className="lg-error"><IcAlert /> {errors.password}</span>
            )}
          </label>

          <button type="submit" className="lg-submit" disabled={submitting}>
            {submitting ? "Signing in…" : (<><span>Sign in</span> <IcArrow /></>)}
          </button>

          <div className="lg-divider"><span>or</span></div>

          <button
            type="button"
            className="lg-whatsapp"
            onClick={handleWhatsAppContact}
          >
            <IcWA /> Need help? Contact us on WhatsApp
          </button>

          <p className="lg-form__foot">
            By signing in you agree to Protour's terms of service and acceptable
            use policy.
          </p>
        </form>
      </main>
    </div>
  );
};

export default Login;
